import { isReservedBusinessSlug } from "../util/businessSlug";
// One canonical list for everything: ids, labels, flags, route bases.
// Add/edit verticals here ONLY.

import type { BusinessData } from "../types";

/** ------------------ Label contract (enforced for all verticals) ------------------ */
export type VerticalLabels = {
  appTitle: string;
  providerSingular: string;
  providerPlural: string;
  inventorySingular: string;
  inventoryPlural: string;
  leadSingular: string;
  leadPlural: string;
  searchHeader1: string;
  searchHeader2: string;
  businessLandingPageH1: string;
  businessLandingPageH2: string;
};

/** ------------------ Core shape for a vertical ------------------ */
export type VerticalDef = {
  id: string; // e.g., "care", "booking", "rentals"
  routeBase: string; // e.g., "care", "booking"
  title: string; // UI display name for the switcher
  showBookingTypes: boolean; // feature toggle used in cards/pages
  labels: VerticalLabels; // all UI labels for the vertical
  mirroredSubpaths: readonly string[]; // safe subpaths to preserve when switching
};

/** ------------------ Helpers ------------------ */
function normalizeSlug(value: unknown): string | null {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  let pathish = raw;
  if (/^https?:\/\//i.test(pathish)) {
    try {
      pathish = new URL(pathish).pathname || "";
    } catch {
      // Fall back to raw value below.
    }
  }

  const trimmed = pathish.replace(/^\/+|\/+$/g, "");
  if (!trimmed) return null;

  const parts = trimmed.split("/").filter(Boolean);
  if (!parts.length) return null;
  if (parts[0] === "b" && parts[1]) return parts[1];
  return parts[0];
}

const getUrlSlug = (b: any) =>
  normalizeSlug(b?.businessBasic?.url ?? b?.url ?? null);

function buildPath(...segs: (string | number | null | undefined)[]) {
  return (
    "/" +
    segs
      .filter((s) => s || s === 0)
      .map((s) => encodeURIComponent(String(s)))
      .join("/")
  ).replace(/\/+/g, "/");
}

/** ------------------ Canonical list (edit me) ------------------ */
export const VERTICALS = [
  {
    id: "care",
    routeBase: "care",
    title: "Care",
    showBookingTypes: false,
    labels: {
      appTitle: "Plany365 Care",
      providerSingular: "Care Provider",
      providerPlural: "Care Providers",
      inventorySingular: "Bed",
      inventoryPlural: "Beds",
      leadSingular: "Referral",
      leadPlural: "Referrals",
      searchHeader1: "Find Care Homes. Send Referrals. Move In Faster.",
      searchHeader2:
        "Discover care providers and send resident referrals with confidence",
      businessLandingPageH1: "Showcase Your Adult Care",
      businessLandingPageH2:
        "Publish live room and bed availability, schedule family tours, capture referrals from families, hospitals, agencies, and social workers, hire caregivers with ease, streamline bookings, and build trust by showcasing licensing, care levels, and amenities-no PHI required.",
    },
    mirroredSubpaths: [
      "",
      "leads",
      "providers",
      "inventory",
      "availability",
      "invoices",
      "reports",
      "settings",
    ] as const,
  },
  {
    id: "booking", // Calendly-style direct booking
    routeBase: "booking",
    title: "Booking (Timely365)",
    showBookingTypes: true,
    labels: {
      appTitle: "Booking (Timely365)",
      providerSingular: "Host",
      providerPlural: "Hosts",
      inventorySingular: "Event Type",
      inventoryPlural: "Event Types",
      leadSingular: "Booking",
      leadPlural: "Bookings",
      searchHeader1: "Easy Booking, Secure Payments & Tipping",
      searchHeader2:
        "Create, book, and manage events, services, and classes, all in one place",
      businessLandingPageH1: "Launch Your Booking Page",
      businessLandingPageH2:
        "Create a shareable booking link in minutes. Take secure payments and send reminders for services, classes, events, and 1-on-1s.",
    },
    mirroredSubpaths: ["", "providers", "settings"] as const,
  },
  // {
  //   id: "wallet",
  //   routeBase: "wallet",
  //   title: "PlanyCoin Wallet",
  //   showBookingTypes: false,
  //   labels: {
  //     appTitle: "Plany365 Coins",
  //     providerSingular: "Host",
  //     providerPlural: "Hosts",
  //     inventorySingular: "Event Type",
  //     inventoryPlural: "Event Types",
  //     leadSingular: "Transaction",
  //     leadPlural: "Transactions",
  //     searchHeader1: "Your balance, tips & rewards",
  //     searchHeader2:
  //       "Top up, tip with PlanyCoin, and track all payments in one place. PlanyCoin is in-app credit (not a cryptocurrency).",
  //     businessLandingPageH1: "Enable Your PlanyCoin Wallet",
  //     businessLandingPageH2:
  //       "Accept tips and rewards seamlessly across Plany365—no crypto, just simple in-app credit.",
  //   },
  //   mirroredSubpaths: ["", "settings"] as const,
  // },
  // Add rentals later by just appending another item here:
  // {
  //   id: "rentals",
  //   routeBase: "rentals",
  //   title: "Property Leasing",
  //   showBookingTypes: false,
  //   labels: { ...same shape... },
  //   mirroredSubpaths: ["", "providers", "inventory", "settings"] as const,
  // },
] as const satisfies readonly VerticalDef[];

/** ------------------ Derived types (no redeclaration anywhere else) ------------------ */
export type Vertical = (typeof VERTICALS)[number];
export type VerticalId = Vertical["id"];

/** ------------------ Fast lookups & helpers derived from the list ------------------ */
export const VERTICAL_BY_ID: Record<VerticalId, Vertical> = VERTICALS.reduce(
  (acc, v) => ((acc[v.id] = v), acc),
  {} as Record<VerticalId, Vertical>
);

export const VERTICAL_IDS = VERTICALS.map((v) => v.id) as VerticalId[];

export const LABELS: Record<VerticalId, VerticalLabels> = VERTICALS.reduce(
  (acc, v) => ((acc[v.id] = v.labels), acc),
  {} as Record<VerticalId, VerticalLabels>
);

/** Build slug-first public URL: "/:slug/<routeBase>" or "/<routeBase>" if no slug */
export function buildPublicUrl(vId: VerticalId, b?: BusinessData | null) {
  const slug = b ? getUrlSlug(b) : null;
  const base = VERTICAL_BY_ID[vId].routeBase;
  return slug ? (vId === "booking" ? buildPath(slug) : buildPath(slug, base)) : buildPath(base);
}

/** Canonical public workspace URL. */
export function buildBusinessUrlCanonical(b?: BusinessData | null) {
  const slug = b ? getUrlSlug(b) : null;
  return slug ? buildPath(slug) : null;
}

/** Card adapter derived from registry */
export const HOME_ADAPTERS = Object.fromEntries(
  VERTICALS.map((v) => [
    v.id,
    {
      getCardUrl: (b: BusinessData) => buildPublicUrl(v.id as VerticalId, b),
      showBookingTypes: v.showBookingTypes,
    },
  ])
) as Record<
  VerticalId,
  { getCardUrl: (b: BusinessData) => string | null; showBookingTypes: boolean }
>;

/** Detect vertical from a pathname (supports "/<base>" and "/:slug/<base>") */
export function detectVerticalFromPath(pathname: string): VerticalId | null {
  const normalizedPathname = String(pathname || "");
  const firstSegment = normalizedPathname.split("/").filter(Boolean)[0];
  if (firstSegment && (firstSegment === "b" || !isReservedBusinessSlug(firstSegment))) return "booking";
  if (/^\/biz\/(?:bookingtypes|calendar|capture)(?:\/|$)/.test(normalizedPathname)) {
    return "booking";
  }
  if (/^\/biz\/(?:dashboard|residents|manage\/jobs)(?:\/|$)/.test(normalizedPathname)) {
    return "care";
  }

  for (const v of VERTICALS) {
    const re = new RegExp(`^/(?:${v.routeBase}|[^/]+/${v.routeBase})(?:/|$)`);
    if (re.test(normalizedPathname)) return v.id;
  }
  return null;
}

/** Get mirrored subpaths set for quick checks (used by switchers) */
export const MIRRORED_SUBPATHS = new Set(
  Array.from(new Set(VERTICALS.flatMap((v) => v.mirroredSubpaths)))
);

/** (Optional) reserved top paths to protect against slug collisions */
export const RESERVED_TOP = new Set<string>([
  ...VERTICALS.map((v) => v.routeBase),
  "b",
  "biz",
  "book",
  "embed",
  "login",
  "signup",
  "search",
  "about",
  "privacy",
  "terms",
  "faq",
  "api",
  "graphql",
]);

/** (Optional) type guard to safely narrow strings to VerticalId where needed */
export function isVerticalId(id: string): id is VerticalId {
  return Object.prototype.hasOwnProperty.call(VERTICAL_BY_ID, id);
}
